@if (current(); as delivery) {
  <button type="button" cdkOverlayOrigin #origin="cdkOverlayOrigin"
          data-testid="webhook-delivery-trigger"
          class="inline-flex items-center justify-center w-7 h-7 rounded-full cursor-pointer hover:bg-ds-primary/5 transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-secondary"
          [class]="presentation().toneClass"
          [matTooltip]="summary()"
          [attr.aria-label]="summary() + '. Show webhook details'"
          aria-haspopup="dialog"
          [attr.aria-expanded]="open()"
          (click)="toggle()">
    <mat-icon aria-hidden="true" class="!text-[18px] !w-[18px] !h-[18px] !leading-none motion-reduce:animate-none"
              [class.animate-spin]="delivery.state === 'retrying'">{{ presentation().icon }}</mat-icon>
  </button>

  <ng-template cdkConnectedOverlay
               [cdkConnectedOverlayOrigin]="origin"
               [cdkConnectedOverlayOpen]="open()"
               [cdkConnectedOverlayPositions]="popoverPositions"
               [cdkConnectedOverlayHasBackdrop]="true"
               cdkConnectedOverlayBackdropClass="cdk-overlay-transparent-backdrop"
               (backdropClick)="close()"
               (detach)="close()">
    <section role="dialog" aria-labelledby="webhook-delivery-title" cdkTrapFocus cdkTrapFocusAutoCapture
             data-testid="webhook-delivery-panel"
             class="w-80 max-w-[calc(100vw-2rem)] bg-ds-surface rounded-ds-md border border-ds-border shadow-lg p-3 text-sm">
      <div class="flex items-center justify-between gap-2 mb-2">
        <h3 id="webhook-delivery-title" class="text-sm font-semibold font-data text-ds-text">Webhook</h3>
        <button type="button" aria-label="Close webhook details" data-testid="webhook-delivery-close-btn"
                class="inline-flex items-center justify-center w-7 h-7 rounded-full text-ds-text-muted hover:bg-ds-primary/5 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-secondary"
                (click)="close()">
          <mat-icon aria-hidden="true" class="!text-[16px] !w-4 !h-4 !leading-4">close</mat-icon>
        </button>
      </div>

      <div class="space-y-1">
        <p class="flex items-center gap-1.5 font-semibold font-data" [class]="presentation().toneClass"
           role="status" aria-live="polite" data-testid="webhook-delivery-state">
          <mat-icon aria-hidden="true" class="!text-[18px] !w-[18px] !h-[18px] !leading-none">{{ presentation().icon }}</mat-icon>
          {{ presentation().label }}
        </p>

        @if (lastAttemptAt(); as attemptedAt) {
          <p class="text-xs text-ds-text-muted font-data tabular-nums" data-testid="webhook-delivery-last-attempt">
            Last attempt {{ attemptedAt | date:'medium' }}
            @if (delivery.trigger === 'manual') {
              <span>· sent manually</span>
            }
          </p>
        }

        @if (delivery.state !== 'delivered' && (delivery.error || delivery.httpStatus)) {
          <p class="text-xs font-data text-ds-danger break-words" data-testid="webhook-delivery-error">
            {{ delivery.error || ('HTTP ' + delivery.httpStatus) }}
          </p>
        } @else if (delivery.state === 'delivered' && delivery.httpStatus) {
          <p class="text-xs font-data text-ds-text-muted" data-testid="webhook-delivery-http-status">HTTP {{ delivery.httpStatus }}</p>
        }

        @if (delivery.targetHost) {
          <p class="flex flex-wrap items-center gap-1.5 text-xs text-ds-text-muted" data-testid="webhook-delivery-target">
            <mat-icon aria-hidden="true" class="!text-[14px] !w-3.5 !h-3.5 !leading-none">arrow_forward</mat-icon>
            <span class="font-data text-ds-text break-all">{{ delivery.targetHost }}</span>
            @if (targetSourceLabel(); as sourceLabel) {
              <span class="px-1.5 py-0.5 rounded-full border border-ds-border text-[11px] leading-none">{{ sourceLabel }}</span>
            }
          </p>
        }
      </div>

      @if (delivery.state !== 'held' && delivery.state !== 'not_configured') {
        <div class="flex items-center justify-end gap-3 mt-3 pt-3 border-t border-ds-border">
          @if (canForceRetry()) {
            <button type="button" data-testid="webhook-delivery-force-btn"
                    class="text-xs text-ds-text-muted underline underline-offset-2 hover:text-ds-text cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-secondary rounded-ds-sm"
                    [disabled]="sending()"
                    (click)="forceRetry()">
              Override and resend
            </button>
          }
          <button type="button" data-testid="webhook-delivery-retry-btn"
                  class="inline-flex items-center gap-1 px-2.5 h-8 text-xs font-medium rounded-ds-sm border border-ds-primary text-ds-primary bg-ds-surface hover:bg-ds-primary/5 cursor-pointer transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-secondary"
                  [disabled]="!canRetry() || sending()"
                  [attr.aria-busy]="sending()"
                  [matTooltip]="canRetry() ? 'Send the document’s current state to the webhook again' : 'Only a failed delivery can be retried directly'"
                  (click)="retry()">
            <mat-icon aria-hidden="true" class="!text-[16px] !w-4 !h-4 !leading-4 motion-reduce:animate-none"
                      [class.animate-spin]="sending()">{{ sending() ? 'autorenew' : 'replay' }}</mat-icon>
            {{ sending() ? 'Sending…' : 'Retry now' }}
          </button>
        </div>
      }
    </section>
  </ng-template>
}
